import { useState, useEffect } from 'react';
import { Fab, Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { useAuthContext } from 'src/auth/hooks';

import { Iconify } from '../iconify';

// ----------------------------------------------------------------------

export function QRScanButton() {
  const [isScanning, setIsScanning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const { authenticated, loading, user } = useAuthContext();

  // Detect if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      return mobileRegex.test(userAgent) || window.innerWidth <= 768;
    };

    setIsMobile(checkIfMobile());

    const handleResize = () => {
      setIsMobile(checkIfMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if current route is a public/auth route
  const isPublicRoute = () => {
    const publicPaths = [
      '/jwt/login',
      '/jwt/sign-up',
      '/jwt/verify',
      '/jwt/reset-password',
      '/jwt/update-password',
      '/auth0/login',
      '/auth0/sign-up',
      '/amplify/login',
      '/amplify/sign-up',
      '/amplify/verify',
      '/amplify/reset-password',
      '/amplify/update-password',
      '/firebase/login',
      '/firebase/sign-up',
      '/supabase/login',
      '/supabase/sign-up',
      '/qrcode/view/', // QR code view pages
    ];

    return publicPaths.some(
      (path) => location.pathname === path || location.pathname.startsWith(path)
    );
  };

  const handleQRScan = () => {
    setIsScanning(true);

    // Call Flutter QR scanner with redirect path
    if (window.flutter_inappwebview?.callHandler) {
      window.flutter_inappwebview.callHandler('qrButtonPressed', {
        redirectPath: '/qrcode/view/',
        queryParams: 'isAuthorizedUser=true',
        api: 'https://url.dgnxt.in/fetch-link/',
      });
    } else {
      console.warn('❌ Flutter InAppWebView handler not available');
    }

    setIsScanning(false);
  };

  // Don't render if:
  // 1. Not on mobile device
  // 2. User is not authenticated (and not loading)
  // 3. On public/auth routes
  // 4. User is not a technician, admin, or assetadmin
  if (
    !isMobile ||
    !authenticated ||
    loading ||
    isPublicRoute() ||
    !user?.role?.some((item) => ['technician', 'admin', 'assetadmin'].includes(item?.name))
  ) {
    return null;
  }

  return (
    <Tooltip title="Scan QR Code" placement="left">
      <Fab
        onClick={handleQRScan}
        disabled={isScanning}
        sx={{
          position: 'fixed',
          bottom: { xs: 120, md: 120 }, // Position above chatbot
          right: { xs: 24, md: 24 },
          zIndex: 1301, // Same as chatbot z-index
          bgcolor: '#8A2BE2',
          color: 'white',
          width: 56,
          height: 56,
          '&:hover': {
            bgcolor: '#7B1FA2',
          },
          '&.Mui-disabled': {
            bgcolor: 'grey.400',
            color: 'grey.600',
          },
        }}
      >
        <Iconify
          icon={isScanning ? 'eos-icons:loading' : 'solar:qr-code-bold'}
          width={24}
          height={24}
        />
      </Fab>
    </Tooltip>
  );
}
